import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import useDomesticHelperFlowSimple from "../../../../hooks/useDomesticHelperFlowSimple";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import SignInModal from "../../../../pages/motorInsurancePages/newMotorPolicy/planPages/signInModal";
import {
  saveDomesticHelperFormData,
  selectDomesticHelperQuoteData,
} from "../../../../redux/features/domesticHelperFormSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { formatPremiumWithCurrency } from "../../../../utils/index";
import FileUpload from "../../../common/fileUploadInput/fileUploadInput";
import FileUploadInput from "../../../common/newFileUploadInput/FileUploadInput";
import DomesticHelperSummaryModal from "../../../newDomesticHelperPolicy/modals/domesticHelperSummaryModal";
import TitleDescription from "../../../newMotorPolicy/titleDescription/titleDescription";
import DomesticHelperBottomStickyBtnLayout from "../../layouts/DomesticHelperBottomStickyBtnLayout";
import DomesticHelperJourneyLayout from "../../layouts/DomesticHelperJourneyLayout";
import "./documents.css";

const Documents = () => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { handleNextStep, handlePrevSubstep } = useDomesticHelperFlowSimple();

  // Check if RTL for currency formatting
  const isRTL = i18n.resolvedLanguage === "ar";

  // Check login status
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const isLoggedIn = !!loginTokenInfo;

  // Get form data from Redux - use the correct form name
  const helperData =
    useSelector((state) => state.domesticHelperForm?.ABOUT_YOUR_HELPER_FORM) ||
    {};
  const applicationData =
    useSelector(
      (state) =>
        state.domesticHelperForm?.DOMESTIC_HELPER_APPLICATION_DETAILS_FORM
    ) || {};

  // Get quote data from Redux (from About Your Helper page API call)
  const quoteData = useSelector(selectDomesticHelperQuoteData);

  const [errors, setErrors] = useState({});
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  // Document upload states
  const [cprUploaded, setCprUploaded] = useState(false);
  const [cprFiles, setCprFiles] = useState([]);
  const [workPermitUploaded, setWorkPermitUploaded] = useState(false);

  // Work permit validation states
  const [workPermitProcessing, setWorkPermitProcessing] = useState(false);
  const [workPermitValid, setWorkPermitValid] = useState(false);
  const [workPermitData, setWorkPermitData] = useState(null);

  // Work permit file data for persistence
  const [workPermitFile, setWorkPermitFile] = useState(null);

  // Get saved document data from Redux
  const documentsData =
    useSelector(
      (state) => state.domesticHelperForm?.DOMESTIC_HELPER_DOCUMENTS_FORM
    ) || {};

  // Debug: Check the entire Redux state structure
  const entireReduxState = useSelector((state) => state.domesticHelperForm);

  useEffect(() => {
    console.log("=== REDUX DEBUG ===");
    console.log("Full domesticHelperForm state:", entireReduxState);
    console.log("DOMESTIC_HELPER_DOCUMENTS_FORM specifically:", documentsData);
    console.log("=== END REDUX DEBUG ===");
  }, [entireReduxState, documentsData]);

  // Initialize component
  useEffect(() => {
    console.log("Documents page loaded");
    console.log("Helper data:", helperData);
    console.log("Has CPR:", !!helperData?.cprNumber);
    console.log("Saved documents data:", documentsData);

    // Restore work permit file state if available
    if (documentsData.workPermitFile) {
      console.log(
        "Restoring work permit file data:",
        documentsData.workPermitFile
      );
      setWorkPermitFile(documentsData.workPermitFile);
      setWorkPermitUploaded(documentsData.workPermitUploaded || false);
      setWorkPermitValid(documentsData.workPermitValid || false);
      setWorkPermitData(documentsData.workPermitData || null);
    }

    // Restore CPR files state if available
    if (documentsData.cprFiles) {
      console.log("Restoring CPR files data:", documentsData.cprFiles);
      setCprFiles(documentsData.cprFiles);
      setCprUploaded(documentsData.cprUploaded || false);
    }
  }, []); // Remove helperData dependency to prevent infinite loop

  const handleContinue = () => {
    // Check if user is logged in before proceeding
    if (!isLoggedIn) {
      setShowSignInModal(true);
      return;
    }

    // Save current step data (synchronous dispatch)
    dispatch(
      saveDomesticHelperFormData({
        formName: "DOMESTIC_HELPER_DOCUMENTS_FORM",
        data: {
          documentsCompleted: true,
          cprUploaded,
          cprFiles,
          workPermitUploaded,
          workPermitValid,
          workPermitData,
          workPermitFile,
          timestamp: new Date().toISOString(),
        },
      })
    );

    // Navigate to next step
    handleNextStep();
  };

  const handleCloseSignInModal = () => {
    setShowSignInModal(false);
  };

  const handleSignInSuccess = () => {
    setShowSignInModal(false);

    // Save current step data after successful login (synchronous dispatch)
    dispatch(
      saveDomesticHelperFormData({
        formName: "DOMESTIC_HELPER_DOCUMENTS_FORM",
        data: {
          documentsCompleted: true,
          cprUploaded,
          cprFiles,
          workPermitUploaded,
          workPermitValid,
          workPermitData,
          workPermitFile,
          timestamp: new Date().toISOString(),
        },
      })
    );

    // Automatically proceed to next step
    handleNextStep();
  };

  // Document upload handlers
  const handleCprUpload = (uploaded, fileName) => {
    console.log("CPR Upload status:", uploaded);
    setCprUploaded(uploaded);
  };

  // CPR file upload handler - new version to track actual files
  const handleCprFileSelect = (fileData) => {
    console.log("CPR File selected:", fileData);

    setCprFiles((prevFiles) => {
      // Enforce max 2 files rule - only show error when trying to upload MORE than 2
      if (prevFiles.length >= 2) {
        console.warn("Maximum 2 CPR files allowed - cannot upload more");
        // toast.error(
        //   translate(
        //     "Maximum 2 CPR files allowed",
        //     "الحد الأقصى ملفين لبطاقة الهوية"
        //   )
        // );
        return prevFiles; // Don't add more files
      }

      const updatedFiles = [...prevFiles, fileData];

      // Save to Redux with updated files
      dispatch(
        saveDomesticHelperFormData({
          formName: "DOMESTIC_HELPER_DOCUMENTS_FORM",
          data: {
            ...documentsData,
            cprFiles: updatedFiles,
            cprUploaded: true,
            timestamp: new Date().toISOString(),
          },
        })
      );

      return updatedFiles;
    });

    setCprUploaded(true);
  };

  // CPR file remove handler - handle individual file removal
  const handleCprFileRemove = (fileToRemove) => {
    console.log("CPR File being removed:", fileToRemove);

    setCprFiles((prevFiles) => {
      // If fileToRemove is provided, remove specific file; otherwise remove all
      const updatedFiles = fileToRemove
        ? prevFiles.filter((file) => file.id !== fileToRemove.id)
        : [];

      const stillHasFiles = updatedFiles.length > 0;

      // Save to Redux with updated files
      dispatch(
        saveDomesticHelperFormData({
          formName: "DOMESTIC_HELPER_DOCUMENTS_FORM",
          data: {
            ...documentsData,
            cprFiles: updatedFiles,
            cprUploaded: stillHasFiles,
            timestamp: new Date().toISOString(),
          },
        })
      );

      return updatedFiles;
    });

    // Update upload status based on remaining files
    setCprUploaded((prevUploaded) => {
      if (!fileToRemove) {
        return false; // Remove all files
      }
      return cprFiles.length > 1; // Keep true if more than 1 file remains
    });
  };

  const handleWorkPermitUpload = async (
    uploaded,
    fileName,
    workPermitOcrData = null,
    fileSize = null
  ) => {
    console.log(
      "Work Permit Upload status:",
      uploaded,
      "OCR Data:",
      workPermitOcrData
    );

    if (uploaded && fileName) {
      // Create file data object for persistence
      const fileInfo = {
        fileName: fileName, // This is now the actual file name with extension
        fileSize: fileSize || "Unknown", // Use provided file size or fallback to Unknown
        file: { name: fileName },
        uploadedAt: new Date().toISOString(),
      };

      console.log("Created fileInfo object:", fileInfo);

      // Store file data for persistence
      setWorkPermitFile(fileInfo);
      setWorkPermitUploaded(true);

      console.log("Set workPermitFile state to:", fileInfo);

      // If we have OCR data from the FileUpload component
      if (workPermitOcrData) {
        console.log("Processing work permit OCR data:", workPermitOcrData);

        // The OCR processing was already done in FileUpload component
        if (workPermitOcrData.isValid) {
          // Work permit is valid - save data and proceed automatically
          setWorkPermitValid(true);
          setWorkPermitData(workPermitOcrData.data);
          setWorkPermitProcessing(false);

          // Save document data to Redux (including file data for persistence)
          console.log("Saving work permit data to Redux:", {
            documentsCompleted: true,
            cprUploaded,
            workPermitUploaded: true,
            workPermitValid: true,
            workPermitData: workPermitOcrData.data,
            workPermitFile: fileInfo,
          });
          dispatch(
            saveDomesticHelperFormData({
              formName: "DOMESTIC_HELPER_DOCUMENTS_FORM",
              data: {
                documentsCompleted: true,
                cprUploaded,
                workPermitUploaded: true,
                workPermitValid: true,
                workPermitData: workPermitOcrData.data,
                workPermitFile: fileInfo,
                timestamp: new Date().toISOString(),
              },
            })
          );

          // Show success message but don't auto-navigate
          toast.success(
            translate(
              "Work permit validated successfully!",
              "تم التحقق من تصريح العمل بنجاح!"
            )
          );
        } else {
          // Work permit is invalid
          setWorkPermitValid(false);
          setWorkPermitProcessing(false);

          toast.error(
            translate(
              "The work permit is invalid. Please upload a valid work permit.",
              "فشل التحقق من تصريح العمل. يرجى تحميل تصريح عمل صالح."
            )
          );
        }
      } else {
        // No OCR data (file restored or OCR failed) - still save file info
        console.log(
          "Work permit uploaded without OCR data - saving file info anyway"
        );
        setWorkPermitProcessing(false);

        // Save file info to Redux even without OCR data
        dispatch(
          saveDomesticHelperFormData({
            formName: "DOMESTIC_HELPER_DOCUMENTS_FORM",
            data: {
              documentsCompleted: false,
              cprUploaded,
              workPermitUploaded: true,
              workPermitValid: false,
              workPermitData: null,
              workPermitFile: fileInfo,
              timestamp: new Date().toISOString(),
            },
          })
        );
      }
    } else {
      // File removed
      setWorkPermitUploaded(false);
      setWorkPermitValid(false);
      setWorkPermitProcessing(false);
      setWorkPermitData(null);
      setWorkPermitFile(null);

      // Update Redux to remove file data
      dispatch(
        saveDomesticHelperFormData({
          formName: "DOMESTIC_HELPER_DOCUMENTS_FORM",
          data: {
            documentsCompleted: false,
            cprUploaded,
            workPermitUploaded: false,
            workPermitValid: false,
            workPermitData: null,
            workPermitFile: null,
            timestamp: new Date().toISOString(),
          },
        })
      );
    }
  };

  const handleShowSummary = () => {
    setShowSummaryModal(true);
  };

  // Get premium from API data (NO frontend calculations)
  const totalPremium = quoteData?.data?.totalPremium
    ? parseFloat(quoteData.data.totalPremium)
    : 0;

  const isContinueDisabled = () => {
    // Disable if work permit is being processed
    if (workPermitProcessing) {
      return true;
    }

    // Document upload validation
    if (helperData?.cprNumber && !cprUploaded) {
      return true; // CPR upload required if helper has CPR number
    }

    // Work Permit must be uploaded AND valid
    if (!workPermitUploaded || !workPermitValid) {
      return true;
    }

    return false;
  };

  return (
    <div className="documents-page">
      <DomesticHelperJourneyLayout customBackHandler={handlePrevSubstep}>
        {/* SignIn Modal */}
        <SignInModal
          show={showSignInModal}
          setShow={setShowSignInModal}
          cbSuccess={handleSignInSuccess}
        />

        <div className="container mx-auto px-4 pb-6">
          <div className="max-w-2xl mx-auto">
            <TitleDescription
              title={translate("Upload Documents", "تحميل وثائق")}
              description={translate(
                "Kindly upload all necessary documents.",
                "يرجى تحميل جميع الوثائق اللازمة."
              )}
            />

            <div className="mt-8">
              {/* CPR Upload - Conditional based on helper having CPR */}
              {helperData?.cprNumber && (
                <div className="mb-8">
                  <h3 className="!text-[#48535B] text-base !font-normal mb-4">
                    {translate(
                      "Domestic Helper CPR (front & back)",
                      "تحميل بطاقة هوية العامل المنزلي (الأمام والخلف)"
                    )}
                  </h3>

                  <FileUploadInput
                    name="cpr_documents_helper"
                    onFileSelect={handleCprFileSelect}
                    onFileRemove={handleCprFileRemove}
                    validTypes={["image/jpeg", "image/png", "application/pdf"]}
                    maxSizeKB={5000}
                    uploadToServer={isLoggedIn} // Only upload to server if logged in
                    multiple={true}
                    maxFiles={2}
                    initialFiles={cprFiles}
                  />
                </div>
              )}

              {/* Work Permit Upload */}
              <div className="mb-8">
                <h3 className="!text-[#48535B] text-base !font-normal mb-4">
                  {translate(
                    "Work Permit from LMRA",
                    "تصريح عمل من هيئة تنظيم سوق العمل (LMRA)"
                  )}
                </h3>

                <FileUpload
                  name="work_permit_lmra"
                  toggleFileUpload={handleWorkPermitUpload}
                  uploadedFile={workPermitFile}
                  key={workPermitFile?.fileName || "no-file"}
                />

                {/* Work Permit Status Messages - Success message removed */}

                {workPermitUploaded &&
                  !workPermitValid &&
                  !workPermitProcessing && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                          <div className="w-2 h-1 bg-white rounded-full"></div>
                        </div>
                        <p className="text-sm text-red-700">
                          {translate(
                            "Work permit validation failed. Please upload a valid work permit.",
                            "فشل التحقق من تصريح العمل. يرجى تحميل تصريح عمل صالح."
                          )}
                        </p>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>

        <DomesticHelperBottomStickyBtnLayout
          hideContinueBtn={true}
          addonComponent={
            <div className="mr-10 rtl:mt-0 max767:w-full max767:flex max767:mb-2 max767:justify-between ipad:mt-0 rtl:ml-10 rtl:mr-0 flex">
              <button
                onClick={handleShowSummary}
                className="text-[#00384D] text-center text-base not-italic font-semibold underline mr-10 rtl:ml-10 rtl:mr-0"
              >
                {translate("Show Summary", "عرض التسعيرة")}
              </button>
              {isRTL ? (
                <strong className="text-[#1A2024] text-xl font-extrabold flex items-center gap-1 flex-row-reverse">
                  <span>{translate("BHD", "د.ب")}</span>
                  <span>
                    {formatPremiumWithCurrency(totalPremium, "", "en-US")}
                  </span>
                </strong>
              ) : (
                <strong className="text-[#1A2024] text-xl font-extrabold">
                  {formatPremiumWithCurrency(
                    totalPremium,
                    translate("BHD", "د.ب"),
                    "en-US"
                  )}
                </strong>
              )}
            </div>
          }
          AdditionalComponent={
            <button
              onClick={handleContinue}
              disabled={isContinueDisabled()}
              className="mb-0 stickybutton flex w-[200px] flex-col items-center gap-2.5 shrink-0 p-4 bg-[#55C6EF] rounded-[10px] text-[#48535B] text-center text-lg not-italic font-semibold leading-6 tracking-[-0.18px] max767:w-full max767:h-[40px] max767:flex-row max767:justify-center max767:p-0 disabled:bg-gray-400 disabled:text-[#1A2024] disabled:cursor-not-allowed"
            >
              {translate("Continue", "التالي")}
            </button>
          }
        ></DomesticHelperBottomStickyBtnLayout>
      </DomesticHelperJourneyLayout>

      <DomesticHelperSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        helperData={helperData}
        applicationData={applicationData}
        quoteData={quoteData}
      />
    </div>
  );
};

export default Documents;
